import { useCallback, useMemo, useRef } from 'react';

/** Contract §5: `…/typing` at most every 20 s (it moves the handoff deadlines). */
export const TYPING_HEARTBEAT_MS = 20_000;

/**
 * `notify()` on every staff activity forwards to `onHeartbeat` immediately
 * and then at most once per TYPING_HEARTBEAT_MS. `reset()` forgets the last
 * beat (another contact: its first keystroke must be reported at once).
 */
export function useTypingHeartbeat(onHeartbeat: (() => void) | undefined): { notify: () => void; reset: () => void } {
  const lastRef = useRef<number | null>(null);
  const callbackRef = useRef(onHeartbeat);
  callbackRef.current = onHeartbeat;

  const notify = useCallback(() => {
    const callback = callbackRef.current;
    if (!callback) return;
    const now = Date.now();
    if (lastRef.current !== null && now - lastRef.current < TYPING_HEARTBEAT_MS) return;
    lastRef.current = now;
    callback();
  }, []);

  const reset = useCallback(() => {
    lastRef.current = null;
  }, []);

  return useMemo(() => ({ notify, reset }), [notify, reset]);
}
